import React, { CSSProperties } from 'react';
import { SemanticTone, semanticColors } from '../../lib/colors';
import {
    SCHEDULE_APP_LINK_LABEL,
    SCHEDULE_CLASS_LOG_LABEL,
    ScheduleMemberLinkSource,
    resolveScheduleAppLink,
    resolveScheduleClassLog,
} from '../../lib/scheduleMemberLink';

const badgeStyle = (tone: SemanticTone): CSSProperties => ({
    display: 'inline-flex',
    alignItems: 'center',
    height: 20,
    padding: '0 8px',
    borderRadius: 10,
    fontSize: 11,
    fontWeight: 700,
    lineHeight: '20px',
    whiteSpace: 'nowrap',
    backgroundColor: semanticColors[tone].bg,
    color: semanticColors[tone].text,
    border: `1px solid ${semanticColors[tone].border}`,
});

type Props = {
    schedule: ScheduleMemberLinkSource;
    /** 1:2 수업처럼 회원이 여러 명인 hover 카드에서 어떤 회원의 상태인지 구분할 때 사용 */
    memberName?: string;
    style?: CSSProperties;
};

/**
 * 스케줄 hover 카드용 배지.
 * 앱 연동 여부와 수업일지 작성 여부를 함께 보여줘서, 일지가 비어 있는 회원을 볼 때
 * 회원 목록으로 이동해 연동 여부를 다시 확인하지 않아도 되게 한다.
 */
const ScheduleMemberBadges = ({ schedule, memberName, style }: Props) => {
    const appLink = resolveScheduleAppLink(schedule);
    const classLog = resolveScheduleClassLog(schedule);

    if (appLink === 'unknown' && classLog === 'none') return null;

    return (
        <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4, ...style }}>
            {memberName && (
                <span style={{ fontSize: 11, color: semanticColors.neutral.text, marginRight: 2 }}>{memberName}</span>
            )}
            {appLink !== 'unknown' && (
                <span
                    style={badgeStyle(appLink === 'linked' ? 'info' : appLink === 'withdrawn' ? 'danger' : 'neutral')}
                    title={
                        appLink === 'linked'
                            ? '앱에 연동된 회원입니다.'
                            : appLink === 'withdrawn'
                            ? '앱에서 탈퇴한 회원입니다. 센터 운영 기록은 유지되지만 앱 전용 기능은 사용할 수 없습니다.'
                            : '앱 미연동 회원입니다. 수업일지를 작성해도 회원이 앱에서 볼 수 없습니다.'
                    }
                >
                    {SCHEDULE_APP_LINK_LABEL[appLink]}
                </span>
            )}
            {classLog !== 'none' && (
                <span style={badgeStyle(classLog === 'written' ? 'success' : 'warning')}>
                    {SCHEDULE_CLASS_LOG_LABEL[classLog]}
                </span>
            )}
        </span>
    );
};

export default ScheduleMemberBadges;
